import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiCheck, FiDownload, FiUpload, FiX } from "react-icons/fi";
import { API } from "../config/api.js";
import { post, put } from "../lib/http.js";
import { readBbUserContact, sanitizePhoneInputDigits, isValidIndianPhoneInput } from "../utils/phone.js";
import { useAuth } from "../context/AuthContext.jsx";
import {
  ACCOUNT_TYPES,
  AGREEMENT_PDF_PATH,
  BANK_NAMES,
  UPLOAD_KINDS,
  applicationStatus,
  fetchMyOrganizerApplication,
  hasAgreementDoc,
  hasChequeDoc,
  hasPanDoc,
  inferWizardStep,
  isApproved,
  isSuperadmin,
  normalizeApplication,
  allRejectedFieldKeys,
  rejectedFieldLabels,
  parseRejectedFieldsFromRemark,
  REJECTABLE_FIELDS,
  safeDocHref,
} from "../organizer/organizerApplication.js";

const STEPS = [
  { id: 1, label: "General Information" },
  { id: 2, label: "Upload documents" },
  { id: 3, label: "Sign agreement" },
];

const DOC_ACCEPT = ".jpg,.jpeg,.pdf,image/jpeg,application/pdf";
const DOC_TYPES = ["image/jpeg", "application/pdf"];
const PDF_ONLY = ["application/pdf"];
const MAX_BYTES = 2 * 1024 * 1024;

const emptyForm = () => ({
  entityType: "organisation",
  legalName: "",
  pan: "",
  organisationAddress: "",
  hasGstin: null,
  gstin: "",
  filedLastTwoYearsItr: null,
  contact: { fullName: "", email: "", mobile: "" },
  bank: {
    beneficiaryName: "",
    accountType: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
  },
});

function appToForm(app, contactPrefill) {
  const base = emptyForm();
  if (!app) {
    return {
      ...base,
      contact: {
        fullName: contactPrefill.name || "",
        email: contactPrefill.email || "",
        mobile: contactPrefill.mobile || "",
      },
    };
  }
  return {
    entityType: app.entityType || "organisation",
    legalName: app.legalName || "",
    pan: app.pan || "",
    organisationAddress: app.organisationAddress || "",
    hasGstin: typeof app.hasGstin === "boolean" ? app.hasGstin : null,
    gstin: app.gstin || "",
    filedLastTwoYearsItr:
      typeof app.filedLastTwoYearsItr === "boolean" ? app.filedLastTwoYearsItr : null,
    contact: {
      fullName: app.contact?.fullName || contactPrefill.name || "",
      email: app.contact?.email || contactPrefill.email || "",
      mobile: app.contact?.mobile || contactPrefill.mobile || "",
    },
    bank: {
      beneficiaryName: app.bank?.beneficiaryName || "",
      accountType: app.bank?.accountType || "",
      bankName: app.bank?.bankName || "",
      accountNumber: app.bank?.accountNumber || "",
      ifsc: app.bank?.ifsc || "",
    },
  };
}

function validatePan(value) {
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(value || "").trim().toUpperCase());
}

function validateGstin(value) {
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
    String(value || "").trim().toUpperCase()
  );
}

function validateIfsc(value) {
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(value || "").trim().toUpperCase());
}

function validateEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function validateFile(file, { pdfOnly = false } = {}) {
  if (!file) return "Please choose a file.";
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const okType = pdfOnly
    ? PDF_ONLY.includes(type) || name.endsWith(".pdf")
    : DOC_TYPES.includes(type) || /\.(jpe?g|pdf)$/.test(name);
  if (!okType) {
    return pdfOnly
      ? "Upload a PDF only."
      : "Upload JPG, JPEG or PDF only.";
  }
  if (file.size > MAX_BYTES) return "File size must be 2 MB or less.";
  return "";
}

function SectionTitle({ children }) {
  return (
    <div className="rounded-t-xl bg-amber-50 px-4 py-2.5 text-sm font-bold text-slate-800 border-b border-amber-100">
      {children}
    </div>
  );
}

function Field({ label, children, hint, required, flagged }) {
  return (
    <label
      className={[
        "block rounded-xl p-0.5 transition",
        flagged ? "bg-rose-50/80 ring-2 ring-rose-400 ring-offset-1" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        className={[
          "mb-1.5 block text-sm font-semibold",
          flagged ? "text-rose-800" : "text-slate-700",
        ].join(" ")}
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
        {flagged ? (
          <span className="ml-2 inline-flex rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            Needs update
          </span>
        ) : null}
      </span>
      {children}
      {hint ? (
        <span className={`mt-1 block text-xs ${flagged ? "text-rose-700" : "text-slate-500"}`}>
          {hint}
        </span>
      ) : null}
      {flagged ? (
        <span className="mt-1 block text-xs font-semibold text-rose-700">
          This field was flagged in the rejection remark. Please correct it.
        </span>
      ) : null}
    </label>
  );
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 disabled:bg-slate-50";

const flaggedInputClass =
  "w-full rounded-xl border-2 border-rose-400 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-rose-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 disabled:bg-slate-50";

function fieldInputClass(flagged) {
  return flagged ? flaggedInputClass : inputClass;
}

function RadioYesNo({ name, value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-4">
      {[
        [true, "Yes"],
        [false, "No"],
      ].map(([val, label]) => (
        <label key={String(val)} className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            name={name}
            checked={value === val}
            disabled={disabled}
            onChange={() => onChange(val)}
            className="h-4 w-4 border-slate-300 text-amber-600 focus:ring-amber-400"
          />
          {label}
        </label>
      ))}
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="mx-auto flex w-full max-w-3xl items-start">
      {STEPS.map((step, index) => {
        const active = current === step.id;
        const done = current > step.id;
        return (
          <React.Fragment key={step.id}>
            {index > 0 ? (
              <div
                className={[
                  "mt-5 h-1 flex-1 rounded-full transition-colors",
                  current > index ? "bg-gradient-to-r from-amber-400 to-orange-500" : "bg-slate-200",
                ].join(" ")}
              />
            ) : null}
            <div className="flex w-24 flex-col items-center gap-2 sm:w-32">
              <span
                className={[
                  "grid h-10 w-10 place-items-center rounded-full text-sm font-black transition",
                  done
                    ? "bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950"
                    : active
                      ? "bg-slate-900 text-white ring-4 ring-amber-200"
                      : "border-2 border-slate-200 bg-white text-slate-400",
                ].join(" ")}
              >
                {done ? <FiCheck /> : step.id}
              </span>
              <span
                className={[
                  "text-center text-[11px] font-bold leading-tight sm:text-xs",
                  active || done ? "text-slate-900" : "text-slate-400",
                ].join(" ")}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SetupShell({ children, verified = false }) {
  return (
    <div className="min-h-screen bg-[#fffdf8]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-2.5">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-base font-black text-slate-950">
              S
            </span>
            <div className="leading-tight">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-amber-400">ShowTimeBro</div>
              <div className="text-[10px] font-semibold text-white/60">Account Setup</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {verified ? (
              <Link
                to="/listyourshow/manage"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <FiArrowLeft /> My events
              </Link>
            ) : null}
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs font-bold text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              Public website
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">{children}</main>
    </div>
  );
}

function UploadSlot({
  title,
  exampleHref,
  url,
  fileLabel,
  uploading,
  accept,
  onSelect,
  onClearLocal,
  disabled,
  flagged,
}) {
  const href = safeDocHref(url);
  return (
    <div
      className={[
        "rounded-xl border bg-white p-4",
        flagged ? "border-rose-400 ring-2 ring-rose-300 bg-rose-50/40" : "border-slate-200",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={`text-sm font-bold ${flagged ? "text-rose-900" : "text-slate-900"}`}>
          {title}
          {flagged ? (
            <span className="ml-2 inline-flex rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Needs update
            </span>
          ) : null}
        </h3>
        {exampleHref ? (
          <a
            href={exampleHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900"
          >
            View example
          </a>
        ) : null}
      </div>
      {flagged ? (
        <p className="mt-1 text-xs font-semibold text-rose-700">
          This document was flagged — please replace it with a clear valid file.
        </p>
      ) : null}

      {href ? (
        <div
          className={[
            "mt-3 rounded-xl px-3 py-3",
            flagged
              ? "border border-rose-300 bg-rose-50"
              : "border border-emerald-200 bg-emerald-50",
          ].join(" ")}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className={[
                  "grid h-6 w-6 place-items-center rounded-full text-white",
                  flagged ? "bg-rose-500" : "bg-emerald-500",
                ].join(" ")}
              >
                {flagged ? "!" : <FiCheck className="h-3.5 w-3.5" />}
              </span>
              <p className={`text-sm font-semibold ${flagged ? "text-rose-900" : "text-emerald-900"}`}>
                {flagged ? "Needs replacement" : "Uploaded"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={[
                  "inline-flex rounded-lg px-3 py-1.5 text-xs font-bold text-white",
                  flagged ? "bg-rose-700 hover:bg-rose-800" : "bg-slate-900 hover:bg-amber-600",
                ].join(" ")}
              >
                View
              </a>
              <label
                className={[
                  "inline-flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-xs font-bold",
                  flagged
                    ? "border-rose-300 text-rose-900 hover:bg-rose-50"
                    : "border-emerald-300 text-emerald-900 hover:bg-emerald-50",
                ].join(" ")}
              >
                Replace
                <input
                  type="file"
                  accept={accept}
                  className="hidden"
                  disabled={disabled || uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) onSelect(file);
                  }}
                />
              </label>
            </div>
          </div>
        </div>
      ) : fileLabel ? (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-950">Selected</p>
              <p className="truncate text-xs text-amber-900">{fileLabel}</p>
            </div>
            {!uploading && onClearLocal ? (
              <button
                type="button"
                onClick={onClearLocal}
                className="rounded-full p-1 text-amber-800 hover:bg-amber-100"
                aria-label="Clear selection"
              >
                <FiX />
              </button>
            ) : null}
          </div>
          {uploading ? (
            <p className="mt-2 text-xs font-medium text-amber-800">Uploading…</p>
          ) : null}
        </div>
      ) : (
        <label
          className={[
            "mt-3 flex min-h-[7.5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/60 px-4 py-6 text-center transition hover:border-amber-400 hover:bg-amber-50",
            (disabled || uploading) && "pointer-events-none opacity-60",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <FiUpload className="h-6 w-6 text-amber-700" />
          <span className="text-sm font-bold text-amber-900">
            {uploading ? "Uploading…" : "Upload"}
          </span>
          <span className="text-xs text-slate-500">JPG, JPEG or PDF · max 2 MB</span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            disabled={disabled || uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onSelect(file);
            }}
          />
        </label>
      )}
    </div>
  );
}

function StatusScreen({ variant, rejectionReason, flaggedLabels, organizerId, onEdit }) {
  if (variant === "approved") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-2xl text-emerald-700">
          ✓
        </div>
        <h2 className="mt-4 text-2xl font-black text-slate-900">You&apos;re verified</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Your ShowTimeBro organizer account is approved. You can create and manage event listings.
        </p>
        {organizerId ? (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-800">
              Your Organizer ID
            </p>
            <p className="mt-1 text-xl font-black tracking-wide text-slate-900">{organizerId}</p>
          </div>
        ) : null}
        <Link
          to="/listyourshow/new"
          className="mt-6 inline-flex rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-3 text-sm font-extrabold text-slate-950 shadow-sm hover:from-amber-300 hover:to-orange-400"
        >
          Create your event
        </Link>
      </div>
    );
  }

  if (variant === "pending") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-100 text-2xl">
          ⌛
        </div>
        <h2 className="mt-4 text-2xl font-black text-slate-900">Thank you for submitting</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Your account setup is under review. Our team typically completes verification within{" "}
          <strong>1–3 business days</strong>. We&apos;ll notify you once it&apos;s approved.
        </p>
        <p className="mt-4 text-sm text-slate-600">
          Questions? Reach ShowTimeBro at{" "}
          <a
            href="mailto:info@showtimebro.com"
            className="font-semibold text-amber-800 underline underline-offset-2"
          >
            info@showtimebro.com
          </a>{" "}
          or{" "}
          <a href="tel:+917440550105" className="font-semibold text-amber-800 underline underline-offset-2">
            +91 74405 50105
          </a>
          .
        </p>
      </div>
    );
  }

  if (variant === "rejected") {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-rose-100 text-2xl text-rose-700">
          !
        </div>
        <h2 className="mt-4 text-2xl font-black text-slate-900">Application rejected</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Your organizer verification was not approved. Please review the remark below, update your details, and resubmit.
        </p>
        <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-left">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-rose-700">
            Rejection remark
          </p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-rose-950">
            {rejectionReason || "Please update your documents and details, then resubmit for review."}
          </p>
          {Array.isArray(flaggedLabels) && flaggedLabels.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {flaggedLabels.map((label) => (
                <span
                  key={label}
                  className="inline-flex rounded-full bg-rose-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="mt-6 inline-flex rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white hover:bg-amber-600"
        >
          Edit &amp; resubmit
        </button>
      </div>
    );
  }

  return null;
}

export default function AccountSetupPage() {
  const contactPrefill = useMemo(() => readBbUserContact(), []);
  const bypass = isSuperadmin();
  const { user, refreshUser, updateUser } = useAuth() || {};

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => appToForm(null, contactPrefill));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [forceEdit, setForceEdit] = useState(false);
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Snapshot of flagged fields for the whole edit session (survives draft save)
  const [editFlags, setEditFlags] = useState([]);
  const [editReason, setEditReason] = useState("");

  const status = applicationStatus(app);
  const organizerId = app?.organizerId || user?.organizerId || "";

  const beginEditResubmit = useCallback((application) => {
    const keys = allRejectedFieldKeys(application);
    // Also merge any @tokens still in the remark text
    for (const key of parseRejectedFieldsFromRemark(application?.rejectionReason)) {
      if (!keys.includes(key)) keys.push(key);
    }
    setEditFlags(keys);
    setEditReason(application?.rejectionReason || "");
    setForceEdit(true);
    setStep(1);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchMyOrganizerApplication();
      setApp(next);
      setForm(appToForm(next, contactPrefill));
      const nextStatus = applicationStatus(next);
      if (next && (nextStatus === "draft" || nextStatus === "rejected")) {
        setStep(inferWizardStep(next));
      }
      // Resume edit session if draft still carries rejection flags/remark
      if (
        nextStatus === "draft" &&
        (next?.rejectionReason || (Array.isArray(next?.rejectedFields) && next.rejectedFields.length))
      ) {
        const keys = allRejectedFieldKeys(next);
        for (const key of parseRejectedFieldsFromRemark(next?.rejectionReason)) {
          if (!keys.includes(key)) keys.push(key);
        }
        setEditFlags(keys);
        setEditReason(next?.rejectionReason || "");
        setForceEdit(true);
      }
      if (nextStatus === "approved" && next?.organizerId) {
        try {
          if (typeof updateUser === "function") {
            updateUser({ organizerId: next.organizerId });
          } else if (typeof refreshUser === "function") {
            refreshUser();
          }
        } catch {
          /* ignore */
        }
      }
      if (nextStatus === "rejected" && next?.rejectionReason) {
        const notifyKey = `org_reject_toast_${next._id || next.id || "app"}`;
        if (!sessionStorage.getItem(notifyKey)) {
          sessionStorage.setItem(notifyKey, "1");
          toast.info(`Your application was rejected: ${next.rejectionReason}`, {
            autoClose: 9000,
          });
        }
      }
    } catch (error) {
      if (error?.status === 404) {
        setApp(null);
        setForm(appToForm(null, contactPrefill));
        setStep(1);
      } else {
        toast.error(error.message || "Could not load your application.");
      }
    } finally {
      setLoading(false);
    }
  }, [contactPrefill, updateUser, refreshUser]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setContact = (key, value) =>
    setForm((f) => ({ ...f, contact: { ...f.contact, [key]: value } }));
  const setBank = (key, value) =>
    setForm((f) => ({ ...f, bank: { ...f.bank, [key]: value } }));

  const detailsPayload = () => ({
    entityType: form.entityType,
    legalName: String(form.legalName || "").trim(),
    pan: String(form.pan || "").trim().toUpperCase(),
    organisationAddress: String(form.organisationAddress || "").trim(),
    hasGstin: form.hasGstin === true,
    gstin: form.hasGstin ? String(form.gstin || "").trim().toUpperCase() : "",
    filedLastTwoYearsItr: form.filedLastTwoYearsItr === true,
    contact: {
      fullName: String(form.contact.fullName || "").trim(),
      email: String(form.contact.email || "").trim().toLowerCase(),
      mobile: String(form.contact.mobile || "").trim(),
    },
    bank: {
      beneficiaryName: String(form.bank.beneficiaryName || "").trim(),
      accountType: form.bank.accountType,
      bankName: form.bank.bankName,
      accountNumber: String(form.bank.accountNumber || "").trim(),
      ifsc: String(form.bank.ifsc || "").trim().toUpperCase(),
    },
  });

  const validateStep1 = () => {
    const p = detailsPayload();
    if (!p.entityType) return "Select organisation or individual.";
    if (!p.legalName) return "Enter organisation / individual name.";
    if (!validatePan(p.pan)) return "Enter a valid PAN (e.g. ABCDE1234F).";
    if (!p.organisationAddress) return "Enter address.";
    if (form.hasGstin === null) return "Select whether you have a GSTIN.";
    if (form.hasGstin && !validateGstin(p.gstin)) return "Enter a valid GSTIN.";
    if (form.filedLastTwoYearsItr === null) return "Select ITR filing status.";
    if (!p.contact.fullName) return "Enter contact full name.";
    if (!validateEmail(p.contact.email)) return "Enter a valid email address.";
    if (!isValidIndianPhoneInput(p.contact.mobile)) return "Enter a valid 10-digit mobile number.";
    if (!p.bank.beneficiaryName) return "Enter beneficiary name.";
    if (!p.bank.accountType) return "Select account type.";
    if (!p.bank.bankName) return "Select bank name.";
    if (!/^\d{9,18}$/.test(p.bank.accountNumber)) return "Enter a valid account number.";
    if (!validateIfsc(p.bank.ifsc)) return "Enter a valid IFSC code.";
    return "";
  };

  const saveDetails = async ({ proceed } = {}) => {
    const err = validateStep1();
    if (err) {
      toast.warn(err);
      return;
    }
    setSaving(true);
    try {
      const response = await put(API.organizerApplication.details, detailsPayload());
      const next = normalizeApplication(response) || (await fetchMyOrganizerApplication());
      setApp(next);
      setForm(appToForm(next, contactPrefill));
      toast.success("Details saved.");
      if (proceed) setStep(2);
    } catch (error) {
      toast.error(error.message || "Could not save details.");
    } finally {
      setSaving(false);
    }
  };

  const uploadKind = async (kind, file, { pdfOnly = false } = {}) => {
    const err = validateFile(file, { pdfOnly });
    if (err) {
      toast.warn(err);
      return;
    }
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const response = await post(API.organizerApplication.upload(kind), fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = normalizeApplication(response) || (await fetchMyOrganizerApplication());
      setApp(next);
      // Clear red flag for this document after a successful re-upload
      const clearedKey = String(kind || "").trim();
      setEditFlags((prev) => prev.filter((key) => key !== clearedKey));
      toast.success("Document replaced successfully.");
    } catch (error) {
      toast.error(error.message || "Upload failed.");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submitApplication = async () => {
    if (!hasAgreementDoc(app)) {
      toast.warn("Upload the signed agreement PDF first.");
      return;
    }
    if (flagged("signedAgreement")) {
      toast.warn("Please replace the flagged signed agreement before submitting.");
      return;
    }
    if (flagged("panCard") || flagged("cancelledCheque")) {
      toast.warn("Please replace all flagged documents before submitting.");
      setStep(2);
      return;
    }
    if (!ack) {
      toast.warn("Please acknowledge the agreement before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await post(API.organizerApplication.submit, {});
      const next = normalizeApplication(response) || (await fetchMyOrganizerApplication());
      setApp(next);
      setForceEdit(false);
      setEditFlags([]);
      setEditReason("");
      toast.success("Application submitted for review.");
    } catch (error) {
      toast.error(error.message || "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (bypass) {
    return (
      <SetupShell>
        <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-black text-slate-900">Account setup not required</h1>
          <p className="mt-2 text-sm text-slate-600">
            Superadmins can create events without organizer verification.
          </p>
          <Link
            to="/listyourshow/new"
            className="mt-6 inline-flex rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
          >
            Create event
          </Link>
        </div>
      </SetupShell>
    );
  }

  if (loading) {
    return (
      <SetupShell>
        <div className="h-64 animate-pulse rounded-2xl bg-slate-100" aria-label="Loading account setup" />
      </SetupShell>
    );
  }

  // Keep red highlights for the whole Edit & resubmit session (even after draft save).
  // Only use editFlags — do NOT re-parse rejectionReason here, or re-uploads stay red forever.
  const showFlags = forceEdit && (editFlags.length > 0 || Boolean(editReason));
  const flagged = (key) => showFlags && editFlags.includes(key);
  const flaggedLabels = showFlags
    ? REJECTABLE_FIELDS.filter((f) => editFlags.includes(f.key)).map((f) => f.label)
    : [];
  const canProceedDocs =
    hasPanDoc(app) &&
    hasChequeDoc(app) &&
    !flagged("panCard") &&
    !flagged("cancelledCheque");
  const canSubmit =
    hasAgreementDoc(app) &&
    ack &&
    !submitting &&
    !flagged("panCard") &&
    !flagged("cancelledCheque") &&
    !flagged("signedAgreement");

  if (!forceEdit && (status === "pending" || isApproved(app))) {
    return (
      <SetupShell verified={status === "approved"}>
        <PageHeader />
        <div className="mt-8">
          <StatusScreen
            variant={status === "approved" ? "approved" : "pending"}
            organizerId={organizerId}
          />
        </div>
      </SetupShell>
    );
  }

  if (status === "rejected" && !forceEdit) {
    return (
      <SetupShell>
        <PageHeader />
        <div className="mt-8">
          <StatusScreen
            variant="rejected"
            rejectionReason={app?.rejectionReason}
            flaggedLabels={rejectedFieldLabels(app)}
            onEdit={() => beginEditResubmit(app)}
          />
        </div>
      </SetupShell>
    );
  }

  return (
    <SetupShell>
      <PageHeader />
      <div className="mt-10">
        <Stepper current={step} />
      </div>

      {forceEdit && (editReason || flaggedLabels.length > 0) ? (
        <div className="mx-auto mt-6 max-w-3xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <p>
            <strong>Previous rejection:</strong>{" "}
            {editReason || app?.rejectionReason || "Please update your details and resubmit."}
          </p>
          {flaggedLabels.length > 0 ? (
            <p className="mt-2 text-xs font-semibold text-rose-800">
              Flagged fields (highlighted in red): {flaggedLabels.join(", ")}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mx-auto mt-6 max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {step === 1 && (
          <div>
            <div className="border-b border-slate-100">
              <SectionTitle>Organisation Details</SectionTitle>
              <div className="space-y-4 p-4 sm:p-5">
                <Field label="Entity type" required flagged={flagged("entityType")}>
                  <div className="flex flex-wrap gap-4">
                    {[
                      ["organisation", "Organisation"],
                      ["individual", "Individual"],
                    ].map(([value, label]) => (
                      <label key={value} className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="radio"
                          name="entityType"
                          checked={form.entityType === value}
                          onChange={() => setField("entityType", value)}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-400"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="Organisation / Individual Name" required flagged={flagged("legalName")}>
                  <input
                    className={fieldInputClass(flagged("legalName"))}
                    value={form.legalName}
                    onChange={(e) => setField("legalName", e.target.value)}
                    placeholder="Enter your organisation name."
                  />
                </Field>
                <Field label="Organisation / Individual PAN" required flagged={flagged("pan")}>
                  <input
                    className={fieldInputClass(flagged("pan"))}
                    value={form.pan}
                    onChange={(e) => setField("pan", e.target.value.toUpperCase())}
                    placeholder="Enter PAN card number."
                    maxLength={10}
                  />
                </Field>
                <Field label="Organisation / Individual Address" required flagged={flagged("organisationAddress")}>
                  <textarea
                    className={fieldInputClass(flagged("organisationAddress"))}
                    rows={3}
                    value={form.organisationAddress}
                    onChange={(e) => setField("organisationAddress", e.target.value)}
                    placeholder="Enter your organisation address."
                  />
                </Field>
                <Field label="Do you have a GSTIN number?" required flagged={flagged("hasGstin")}>
                  <RadioYesNo
                    name="hasGstin"
                    value={form.hasGstin}
                    onChange={(v) => {
                      setField("hasGstin", v);
                      if (!v) setField("gstin", "");
                    }}
                  />
                </Field>
                {form.hasGstin === true ? (
                  <Field label="GSTIN" required flagged={flagged("gstin")}>
                    <input
                      className={fieldInputClass(flagged("gstin"))}
                      value={form.gstin}
                      onChange={(e) => setField("gstin", e.target.value.toUpperCase())}
                      placeholder="Enter GSTIN."
                      maxLength={15}
                    />
                  </Field>
                ) : null}
                <Field label="Have you filed last 2 years ITR return?" required flagged={flagged("filedLastTwoYearsItr")}>
                  <RadioYesNo
                    name="filedItr"
                    value={form.filedLastTwoYearsItr}
                    onChange={(v) => setField("filedLastTwoYearsItr", v)}
                  />
                </Field>
              </div>
            </div>

            <div className="border-b border-slate-100">
              <SectionTitle>Contact Person Details</SectionTitle>
              <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
                <Field label="Full Name" required flagged={flagged("contactFullName")}>
                  <input
                    className={fieldInputClass(flagged("contactFullName"))}
                    value={form.contact.fullName}
                    onChange={(e) => setContact("fullName", e.target.value)}
                    placeholder="Enter your full name."
                  />
                </Field>
                <Field label="Email address" required flagged={flagged("contactEmail")}>
                  <input
                    type="email"
                    className={fieldInputClass(flagged("contactEmail"))}
                    value={form.contact.email}
                    onChange={(e) => setContact("email", e.target.value)}
                    placeholder="Enter your email address."
                  />
                </Field>
                <Field label="Mobile Number" required flagged={flagged("contactMobile")}>
                  <input
                    className={fieldInputClass(flagged("contactMobile"))}
                    value={form.contact.mobile}
                    onChange={(e) => setContact("mobile", sanitizePhoneInputDigits(e.target.value))}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </Field>
              </div>
            </div>

            <div>
              <SectionTitle>Bank details</SectionTitle>
              <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
                <Field label="Beneficiary Name" required flagged={flagged("bankBeneficiaryName")}>
                  <input
                    className={fieldInputClass(flagged("bankBeneficiaryName"))}
                    value={form.bank.beneficiaryName}
                    onChange={(e) => setBank("beneficiaryName", e.target.value)}
                    placeholder="Enter Beneficiary Name."
                  />
                </Field>
                <Field label="Account Type" required flagged={flagged("bankAccountType")}>
                  <select
                    className={fieldInputClass(flagged("bankAccountType"))}
                    value={form.bank.accountType}
                    onChange={(e) => setBank("accountType", e.target.value)}
                  >
                    <option value="">Select account type.</option>
                    {ACCOUNT_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Bank Name" required flagged={flagged("bankName")}>
                  <select
                    className={fieldInputClass(flagged("bankName"))}
                    value={form.bank.bankName}
                    onChange={(e) => setBank("bankName", e.target.value)}
                  >
                    <option value="">Select bank name.</option>
                    {BANK_NAMES.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Account Number" required flagged={flagged("bankAccountNumber")}>
                  <input
                    className={fieldInputClass(flagged("bankAccountNumber"))}
                    value={form.bank.accountNumber}
                    onChange={(e) =>
                      setBank("accountNumber", e.target.value.replace(/\D/g, "").slice(0, 18))
                    }
                    placeholder="Enter account number."
                    inputMode="numeric"
                  />
                </Field>
                <Field label="Bank IFSC" required flagged={flagged("bankIfsc")}>
                  <input
                    className={fieldInputClass(flagged("bankIfsc"))}
                    value={form.bank.ifsc}
                    onChange={(e) => setBank("ifsc", e.target.value.toUpperCase())}
                    placeholder="Enter Bank IFSC."
                    maxLength={11}
                  />
                </Field>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => saveDetails({ proceed: false })}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:border-amber-400 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save details"}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => saveDetails({ proceed: true })}
                className="rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:opacity-50"
              >
                Proceed
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-4 sm:px-5">
              <p className="text-sm font-semibold text-slate-800">Please make sure that:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                <li>Upload a clear document in .jpg, .jpeg or .pdf format only.</li>
                <li>File size should not be greater than 2 MB.</li>
              </ul>
            </div>
            <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
              <UploadSlot
                title="Upload PAN card"
                url={app?.documents?.panCardUrl}
                uploading={!!uploading[UPLOAD_KINDS.panCard]}
                accept={DOC_ACCEPT}
                flagged={flagged("panCard")}
                onSelect={(file) => uploadKind(UPLOAD_KINDS.panCard, file)}
              />
              <UploadSlot
                title="Upload Cancelled Cheque"
                url={app?.documents?.cancelledChequeUrl}
                uploading={!!uploading[UPLOAD_KINDS.cancelledCheque]}
                accept={DOC_ACCEPT}
                flagged={flagged("cancelledCheque")}
                onSelect={(file) => uploadKind(UPLOAD_KINDS.cancelledCheque, file)}
              />
            </div>
            {(flagged("panCard") || flagged("cancelledCheque")) && (
              <p className="px-4 pb-2 text-sm font-semibold text-rose-700 sm:px-5">
                Replace every red-flagged document before you can proceed.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:border-amber-400"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!canProceedDocs}
                onClick={() => {
                  if (flagged("panCard") || flagged("cancelledCheque")) {
                    toast.warn("Please replace all flagged documents before proceeding.");
                    return;
                  }
                  setStep(3);
                }}
                className="rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                Save details &amp; Proceed
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="px-4 py-6 text-center sm:px-8">
              <p className="mx-auto max-w-lg text-sm leading-6 text-slate-600">
                Upload signed agreement. Click &quot;Download agreement&quot; to download the agreement.
                Upload a .pdf format only. File size should not be greater than 2 MB.
              </p>
              <a
                href={AGREEMENT_PDF_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-amber-800 underline underline-offset-2 hover:text-amber-950"
              >
                <FiDownload /> Download agreement
              </a>

              <div className="mx-auto mt-6 max-w-md">
                <UploadSlot
                  title="Upload signed agreement"
                  url={app?.documents?.signedAgreementUrl}
                  uploading={!!uploading[UPLOAD_KINDS.signedAgreement]}
                  accept=".pdf,application/pdf"
                  flagged={flagged("signedAgreement")}
                  onSelect={(file) =>
                    uploadKind(UPLOAD_KINDS.signedAgreement, file, { pdfOnly: true })
                  }
                />
              </div>

              <label className="mx-auto mt-5 flex max-w-md cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-400"
                />
                <span>
                  I confirm that I have read the ShowTimeBro organizer agreement and the uploaded PDF is
                  a signed copy.
                </span>
              </label>
              {flagged("signedAgreement") ? (
                <p className="mx-auto mt-3 max-w-md text-sm font-semibold text-rose-700">
                  Replace the flagged signed agreement before you can submit.
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:border-amber-400"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!canSubmit}
                onClick={submitApplication}
                className="rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </SetupShell>
  );
}

function PageHeader() {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div className="mb-4 flex items-center justify-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-sm font-black text-slate-950">
          S
        </span>
        <span className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">ShowTimeBro</span>
      </div>
      <h1 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Account Setup</h1>
      <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        Please fill in the details below so we can set up an account for your organisation and give you
        access to list your events on ShowTimeBro.
      </p>
    </div>
  );
}
